import React from "react";
import clsx from "clsx";
import "./badge.styles.scss";
import { useSwitchTransition } from "transition-hook";

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
	animatable?: boolean;
	animateDirection?: "down-up" | "up-down";
	type: "counter" | "dot";
	size?: "small" | "big";
	variant?: "noti-enable" | "noti-disable";
	position?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
	count?: number;
	maxCount?: number;
	content?: string;
	containerClassName?: React.HTMLAttributes<HTMLDivElement>;
	anchor?: boolean;
	show?: boolean;
	verticalOffset?: number;
	horizontalOffset?: number;
}

const Badge = (props: React.PropsWithChildren<BadgeProps>, ref: React.Ref<HTMLDivElement>) => {
	const {
		children,
		className,
		animatable = true,
		animateDirection = "down-up",
		type = "dot",
		size = "big",
		variant = "noti-enable",
		position = "top-right",
		show = true,
		style = {},
		count = 0,
		maxCount,
		content,
		containerClassName,
		anchor,
		verticalOffset,
		horizontalOffset,
		...rest
	} = props;
	const transition = useSwitchTransition(count, 100, "default");
	const badgeContentRef = React.useRef<HTMLDivElement>(null);
	const isAnchor = anchor || !!children;
	const offsetKeys = position.split("-");

	const renderContent = () => {
		if (content) {
			return content;
		}
		if (typeof count !== "number") {
			return null;
		}

		const c = count;
		return animatable ? (
			transition((count, stage) => {
				return (
					<div
						ref={badgeContentRef}
						className={`z-noti-badge__content --${size} --${
							animateDirection === "down-up"
								? "animate-down-up"
								: animateDirection === "up-down"
								? "animate-up-down"
								: ""
						} `}
						style={{
							position: "relative",
							transition: "all 0.1s linear",
							opacity: stage === "enter" ? "1" : "0.4",
							top: {
								from: "24%",
								enter: "0%",
								leave: "-24%",
							}[stage],
						}}
					>
						{count}
					</div>
				);
			})
		) : (
			<div className={`z-noti-badge__content --${size}`}>{c}</div>
		);
	};

	const renderBadge = () => {
		if (!show) {
			return null;
		}
		return (
			<div
				className={clsx(
					`z-noti-badge --${size} --${type} --${variant}`,
					isAnchor && `--anchor --${position}`,
					className,
				)}
				ref={ref}
				style={{
					[offsetKeys[0]]: verticalOffset,
					[offsetKeys[1]]: horizontalOffset,
					...style,
				}}
				{...rest}
			>
				{type === "counter" && renderContent()}
			</div>
		);
	};

	if (!children) {
		return renderBadge();
	}

	return (
		<div className={clsx("z-noti-badge-container", containerClassName)}>
			{renderBadge()}
			{children}
		</div>
	);
};

export default React.forwardRef(Badge);
